import React, { useState, useCallback, useEffect } from 'react';
import type { Vehicle, Id } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { useVehicleListApi, useBestMatchVehiclesApi } from '@hero/api-hooks';
import { useSearch } from '@hero/hooks-shared';
import { cn } from '@hero/web-lib';
import { Search as SearchIcon, Loader2, Plus } from 'lucide-react';
import { Input } from '../../../ui/Input/Input';
import { Button } from '../../../ui/Button';
import { VehicleCardSelectable } from './VehicleCardSelectable';
import { VehicleEditModal } from '../Modal/VehicleEditModal';
import { ObjectId } from 'bson';

type Tab = 'matching' | 'all';

export interface VehiclePickerListProps {
  orderId?: Id;
  selectedVehicle: Vehicle | undefined;
  onVehicleSelected: (vehicle: Vehicle | undefined) => void;
}

export const VehiclePickerList: React.FC<VehiclePickerListProps> = ({
  orderId,
  selectedVehicle,
  onVehicleSelected,
}) => {
  const { t, i18n } = useTranslation(['m-vehicle', 'c-main']);
  const isRTL = i18n.language === 'ar';
  const [tab, setTab] = useState<Tab>(orderId ? 'matching' : 'all');
  const [search, setSearch] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const searchHook = useSearch({
    delay: 300,
    searchValue: search,
    onSearchReadyChange: setSearch,
  });

  const bestMatchQuery = useBestMatchVehiclesApi(
    { orderId: orderId! as ObjectId },
    { enabled: !!orderId },
  );
  const allQuery = useVehicleListApi({ search: search || undefined });

  const bestMatchVehicles = bestMatchQuery.data ?? [];
  const allVehicles = allQuery.data ?? [];

  useEffect(() => {
    if (orderId && !bestMatchQuery.isLoading && bestMatchVehicles.length === 0) {
      setTab('all');
    }
  }, [orderId, bestMatchQuery.isLoading, bestMatchVehicles.length]);

  const activeQuery = tab === 'matching' ? bestMatchQuery : allQuery;
  const vehicles = tab === 'matching' ? bestMatchVehicles : allVehicles;
  const isLoading = activeQuery.isLoading;
  const hasNextPage = activeQuery.hasNextPage ?? false;
  const isFetchingNextPage = activeQuery.isFetchingNextPage ?? false;

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (scrollHeight - scrollTop - clientHeight < 200 && hasNextPage && !isFetchingNextPage) {
        activeQuery.onLoadMore?.();
      }
    },
    [hasNextPage, isFetchingNextPage, activeQuery.onLoadMore],
  );

  const handleSelect = (vehicle: Vehicle) => {
    const isSame = selectedVehicle && String(selectedVehicle._id) === String(vehicle._id);
    onVehicleSelected(isSame ? undefined : vehicle);
  };

  const handleVehicleCreated = (vehicle: Vehicle) => {
    onVehicleSelected(vehicle);
    setIsCreateOpen(false);
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Header: Tabs + Create */}
      <div className="flex items-center gap-2">
        {orderId && (
          <div className="flex flex-1 rounded-lg bg-gray-100 p-0.5 gap-0.5">
            {(['matching', 'all'] as const).map(t_tab => (
              <button
                key={t_tab}
                type="button"
                onClick={() => setTab(t_tab)}
                className={cn(
                  'flex-1 text-xs font-medium py-1.5 rounded-md transition-colors',
                  tab === t_tab
                    ? 'bg-white text-primary shadow-sm'
                    : 'text-muted-foreground hover:text-gray-700',
                )}>
                {t(`picker.tab_${t_tab}`)}
                {t_tab === 'matching' && bestMatchVehicles.length > 0 && (
                  <span className="ms-1 text-[10px] text-muted-foreground">
                    ({bestMatchVehicles.length})
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
        <Button
          variant="outline"
          size="sm"
          className="shrink-0 gap-1 text-xs h-8"
          onClick={() => setIsCreateOpen(true)}>
          <Plus className="w-3.5 h-3.5" />
          {t('c-main:crud_buttons.create_new')}
        </Button>
      </div>

      {/* Search */}
      {tab === 'all' && (
        <div className="relative">
          <SearchIcon
            className={cn(
              'absolute top-2.5 h-4 w-4 text-muted-foreground',
              isRTL ? 'right-2.5' : 'left-2.5',
            )}
          />
          <Input
            placeholder={t('picker.search_placeholder')}
            value={searchHook.inputSearch}
            onChange={searchHook.onInputSearchChange}
            className={cn(
              'h-9 text-sm bg-gray-50 border-gray-200 rounded-lg',
              isRTL ? 'pr-8' : 'pl-8',
            )}
            dir={isRTL ? 'rtl' : 'ltr'}
          />
        </div>
      )}

      {/* Vehicle list */}
      <div
        className="flex flex-col gap-2 max-h-[400px] overflow-y-auto pe-1"
        style={{ overscrollBehavior: 'contain' }}
        onScroll={handleScroll}>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : vehicles.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <p className="text-sm text-muted-foreground">
              {tab === 'matching' ? t('picker.no_matching') : t('picker.no_vehicles')}
            </p>
            {tab === 'matching' && (
              <button
                type="button"
                onClick={() => setTab('all')}
                className="mt-2 text-xs text-primary font-medium hover:underline">
                {t('picker.show_all')}
              </button>
            )}
          </div>
        ) : (
          <>
            {vehicles.map(vehicle => (
              <VehicleCardSelectable
                key={String(vehicle._id)}
                vehicle={vehicle}
                isSelected={
                  !!selectedVehicle && String(selectedVehicle._id) === String(vehicle._id)
                }
                onSelect={handleSelect}
              />
            ))}
            {isFetchingNextPage && (
              <div className="flex items-center justify-center py-3">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            )}
          </>
        )}
      </div>

      {/* Create vehicle modal */}
      <VehicleEditModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onVehicleSaved={handleVehicleCreated}
      />
    </div>
  );
};
